import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import Video, { type VideoProps } from './Video';

export type LazyVideoProps = VideoProps & {
  fallbackSrcs?: string[];
  rootMargin?: string;
  threshold?: number;
};

export default function LazyVideo({
  src,
  fallbackSrcs = [],
  className = '',
  mode = 'autoplay',
  contained = false,
  onLoadedData,
  onError,
  rootMargin = '200px',
  threshold = 0.1,
  ...videoProps
}: LazyVideoProps) {
  const { ref: inViewRef, inView } = useInView({
    rootMargin,
    threshold,
  });
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const [hasError, setHasError] = useState(false);

  const allSources = [src, ...fallbackSrcs];
  const currentSrc = allSources[currentSrcIndex];

  // Reset to first source when src prop changes
  useEffect(() => {
    setCurrentSrcIndex(0);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (currentSrcIndex < allSources.length - 1) {
      setCurrentSrcIndex((i) => i + 1);
    } else {
      setHasError(true);
    }
    onError?.();
  };

  return (
    <div
      ref={inViewRef}
      className={`${className} ${hasError ? 'opacity-50' : ''}`}
    >
      {inView && (
        <Video
          {...videoProps}
          src={currentSrc}
          mode={mode}
          contained={contained}
          className="h-full w-full rounded-[inherit]"
          onLoadedData={onLoadedData}
          onError={handleError}
        />
      )}
    </div>
  );
}
